import React from 'react'
import { useSelector } from 'react-redux'
import { Button, Typography } from '@material-ui/core'
import { Formik, useFormikContext } from 'formik'
import moment from 'moment'
import { SectionLayoutDialog } from 'shared'
import { DATE_FORMAT, STRING_EMPTY, TIME_FORMAT } from 'utils/constants'
import * as Yup from 'yup'

import { InstallmentOption } from '../InstallmentOption'

import { useStyles } from './InstallmentSettingDialog.style'

const INSTALLMENT_MAX = 6

const InstallmentSettingDialog = ({ open, onClose, installmentsState, readOnly }) => {
  const classes = useStyles()

  const { event } = useSelector((state) => state.events)
  const { setValues, values: formState } = useFormikContext()

  const values = {
    installments: installmentsState.length
      ? installmentsState
          .map((item) => ({
            ...item,
            endDateTime: moment(item.endDate)
          }))
          .sort((a, b) => a.number - b.number)
      : [
          { number: 1, amount: STRING_EMPTY, startDate: null, endDate: null, endDateTime: null },
          { number: 2, amount: STRING_EMPTY, startDate: null, endDate: null, endDateTime: null }
        ]
  }

  const validationSchema = Yup.object().shape({
    installments: Yup.array()
      .of(
        Yup.object().shape({
          number: Yup.number().required('Requerido'),
          amount: Yup.number()
            .nullable()
            .positive('Debe ser un número positivo')
            .required('Debe ingresar un monto'),
          startDate: Yup.mixed()
            .required('Ingrese una fecha de inicio')
            .test(
              'startDateBeforeEndDate',
              'La fecha de inicio debe ser anterior a la fecha de fin',
              (value, { parent }) =>
                !parent.endDate || moment(value).isSameOrBefore(moment(parent.endDate))
            )
            .test(
              'test-startDateBeforeToEvent',
              `La fecha de inicio debe ser anterior a ${moment(event.to).format('DD/MM/YYYY')}`,
              (value) =>
                moment(moment(value).format(DATE_FORMAT)).isSameOrBefore(
                  moment(event.to).format(DATE_FORMAT),
                  DATE_FORMAT
                )
            ),
          endDate: Yup.mixed()
            .required('Ingrese una fecha de cierre')
            .test(
              'endDateAfterStartDate',
              'La fecha de fin debe ser posterior a la fecha de inicio',
              (value, { parent }) =>
                !parent.startDate || moment(value).isSameOrAfter(moment(parent.startDate))
            )
            .test(
              'endDateBeforeEventTo',
              `La fecha de cierre debe ser igual o anterior a ${moment(event.to).format(
                'DD/MM/YYYY'
              )}`,
              (value) =>
                moment(moment(value).format(DATE_FORMAT)).isSameOrBefore(
                  moment(event.to).format(DATE_FORMAT),
                  DATE_FORMAT
                )
            ),
          endDateTime: Yup.mixed().required('Requerido')
        })
      )
      .test(
        'noDateOverlap',
        'Las fechas de las cuotas no deben superponerse',
        (installments) =>
          !installments.some((current, index) => {
            if (index === installments.length - 1) return false

            const currentEnd = moment(current.endDate)
            const nextStart = moment(installments[index + 1].startDate)

            return nextStart.isSameOrBefore(currentEnd)
          })
      ),

    installmentList: Yup.mixed()
      .test(
        'atLeastOneInstallment',
        'Las fechas entre cuotas no deben superponerse',
        (value, { parent }) => {
          return !parent.installments.some((current, index) => {
            if (index === parent.installments.length - 1) return false

            const currentEnd = moment(current.endDate)
            const nextStart = moment(parent.installments[index + 1].startDate)
            return nextStart.isSameOrBefore(currentEnd)
          })
        }
      )
      .test(
        'totalAmountMatches',
        'La suma de las cuotas debe ser igual al precio total',
        (value, { parent }) => {
          if (parent.installments.some((installment) => !installment.amount)) return true

          const totalAmount = parent.installments.reduce(
            (sum, installment) => sum + (installment.amount || 0),
            0
          )

          return totalAmount === formState.price
        }
      )
  })

  const handleSubmit = (values) => {
    const firstInstallment = values.installments.sort((a, b) => a.number - b.number)[0]

    setValues((prevValues) => ({
      ...prevValues,
      installments: values.installments.map(
        ({ startDate, endDate, endDateTime, number, ...restProps }) => ({
          ...restProps,
          number,
          startDate: moment(startDate).format(),
          endDate: moment(
            `${moment(endDate).format(DATE_FORMAT)} ${
              number === values.installments.length
                ? moment(endDateTime).format(TIME_FORMAT)
                : moment('23:59', 'HH:mm').format(TIME_FORMAT)
            }`
          ).format()
        })
      ),
      saleFrom: firstInstallment.startDate,
      saleToDate: firstInstallment.endDate,
      saleToTime: moment('23:59', 'HH:mm').format(),
      mercadoPagoEnabled: false
    }))

    onClose(true)
  }

  const addInstallment = (setValues) => (e) =>
    setValues((prevValues) => ({
      ...prevValues,
      installments: [
        ...prevValues.installments,
        {
          number: prevValues.installments.length + 1,
          amount: STRING_EMPTY,
          startDate: null,
          endDate: null,
          endDateTime: null
        }
      ]
    }))

  return (
    <SectionLayoutDialog
      open={open}
      onClose={() => onClose(installmentsState.length)}
      title='CONFIGURAR CUOTAS'>
      <Formik
        enableReinitialize
        initialValues={values}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}>
        {({ values, setValues, handleSubmit, isValid, isSubmitting, errors }) => (
          <form onSubmit={handleSubmit} className={classes.form}>
            <div className={classes.installmentContainer}>
              {values.installments.map((installment, index) => (
                <InstallmentOption
                  key={index}
                  index={index}
                  installment={installment}
                  readOnly={readOnly}
                  installmentsCount={values.installments.length}
                />
              ))}

              {!readOnly && values.installments?.length < INSTALLMENT_MAX && (
                <Typography
                  variant='h6'
                  color='primary'
                  className={classes.addInstallment}
                  onClick={addInstallment(setValues)}>
                  +Añadir cuota
                </Typography>
              )}

              {errors.installmentList && (
                <Typography color='error' variant='caption'>
                  {errors.installmentList}
                </Typography>
              )}
            </div>
            {!readOnly && (
              <Button
                color='primary'
                variant='contained'
                type='submit'
                disabled={!isValid || isSubmitting}
                className={classes.saveButton}>
                Guardar
              </Button>
            )}
          </form>
        )}
      </Formik>
    </SectionLayoutDialog>
  )
}

export default InstallmentSettingDialog
